import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ClientService, Todo } from '../../shared/services/client.service';
import { TenantService } from '../../shared/services/tenant.service';
import { UserService } from '../../shared/services/user.service';
import { NotificationService } from '../../shared/services/notification.service';
import { ConfirmModalComponent } from '../../shared/utils/modal-validation/modal-validation';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-todos',
    standalone: true,
    imports: [CommonModule, FormsModule, ConfirmModalComponent],
    templateUrl: './todos.html',
})
export class TodosComponent implements OnInit, OnDestroy {
    private clientService = inject(ClientService);
    private tenantService = inject(TenantService);
    private userService = inject(UserService);
    private notificationService = inject(NotificationService);
    private route = inject(ActivatedRoute);
    private cdr = inject(ChangeDetectorRef);

    // Data
    allTodos: Todo[] = [];
    filtered: Todo[] = [];
    tenants: any[] = [];
    clients: any[] = [];
    projects: { id: string; projectName: string }[] = []; // for display only
    filteredClientsForTodo: any[] = [];
    filteredProjectsForTodo: any[] = [];
    users: any[] = [];
    currentUser: any = null;
    private assignedTodoIds = new Set<string>();

    // Project search (server-side)
    projectSearch = '';
    filteredProjects: any[] = [];
    selectedProject: any | null = null;
    isSearchingProjects = false;
    private projectSearchTimer: any = null;

    // Filters
    todoTab: 'created' | 'assigned' = 'assigned';
    filterStatus = '';
    filterPriority = '';
    filterTenantId = '';
    filterClientId = '';
    filterProjectId = '';
    filterSearch = '';
    filterBacklog = false;
    private filterSearchTimer: any = null;

    // Form modal
    showModal = false;
    isEditing = false;
    todoType: 'internal' | 'external' = 'internal';
    todoForm: Partial<Todo> = {};
    hasDueDate = false;
    selectedUsers: string[] = [];
    showDeleteConfirm = false;
    todoToDelete: Todo | null = null;
    isLoading = true;

    // Read-only details view ("in-between" view)
    showDetail = false;
    detailTodo: Todo | null = null;
    detailAssignedUsers: any[] = [];

    // Pagination
    private nextToken: string | null = null;
    hasMore = false;
    isLoadingMore = false;

    private subs: Subscription[] = [];

    private _todosInitialized = false;

    async ngOnInit() {
        await Promise.all([
            this.clientService.init(),
            this.tenantService.init(),
            this.userService.init(),
        ]);

        this.subs.push(
            this.tenantService.tenants$.subscribe(tenants => {
                this.tenants = tenants;
            }),
            this.clientService.clients$.subscribe(clients => {
                this.clients = clients;
            }),
            this.userService.users$.subscribe(users => {
                this.users = users;
                this.cdr.markForCheck();
            }),
            this.userService.currentUser$.subscribe(async u => {
                this.currentUser = u;
                if (u?.id && !this._todosInitialized) {
                    this._todosInitialized = true;
                    // Load projects for display names (once)
                    this.loadProjectsForDisplay();
                    // Load first page now that we know the user
                    await this.loadTodos();
                    // Auto-open a todo if navigated from a notification
                    this.checkDeepLink();
                }
            }),
        );
    }

    /** If the URL contains ?todoId=... (preview) or ?new=1 (from the global "New" shortcut), act on it. */
    private checkDeepLink() {
        // Global top-bar "New task" shortcut.
        if (this.route.snapshot.queryParamMap.get('new')) {
            this.openNew();
            return;
        }
        const todoId = this.route.snapshot.queryParamMap.get('todoId');
        if (!todoId) return;
        const todo = this.allTodos.find(t => t.id === todoId);
        if (todo) {
            this.openDetail(todo);
        }
    }

    /** Re-fetch the set of todo IDs the current user is assigned to. */
    private async refreshAssignedIds() {
        if (!this.currentUser?.id) return;
        const ids = await this.clientService.getTodosAssignedToUser(this.currentUser.id);
        this.assignedTodoIds = new Set(ids);
    }

    /** Ensure assignedTodoIds is populated (lazy-loaded, needed only for "created" tab). */
    private async ensureAssignedIds() {
        if (this.assignedTodoIds.size === 0 && this.currentUser?.id) {
            await this.refreshAssignedIds();
        }
    }

    /** Reset accumulated list and fetch the first page for the active tab. */
    async loadTodos() {
        this.isLoading = true;
        this.allTodos = [];
        this.nextToken = null;
        this.cdr.markForCheck();

        let { todos, nextToken } = await this._fetchPage(null);
        // "Created by me" must exclude todos where I'm also an assignee
        if (this.todoTab === 'created') {
            await this.ensureAssignedIds();
            todos = todos.filter(t => !this.assignedTodoIds.has(t.id));
        }
        this.allTodos = todos;
        this.nextToken = nextToken;
        this.hasMore = !!nextToken;
        this.isLoading = false;
        this.applyFilters();
        this.cdr.markForCheck();
    }

    /** Silent refresh: re-fetch data without showing loading spinner or clearing list. */
    private async softRefreshTodos() {
        this.nextToken = null;

        let { todos, nextToken } = await this._fetchPage(null);
        if (this.todoTab === 'created') {
            await this.ensureAssignedIds();
            todos = todos.filter(t => !this.assignedTodoIds.has(t.id));
        }
        this.allTodos = todos;
        this.nextToken = nextToken;
        this.hasMore = !!nextToken;
        this.applyFilters();
        this.cdr.markForCheck();
    }

    /** Append the next page to the existing list. */
    async loadMore() {
        if (!this.hasMore || this.isLoadingMore) return;
        this.isLoadingMore = true;
        this.cdr.markForCheck();

        let { todos, nextToken } = await this._fetchPage(this.nextToken);
        if (this.todoTab === 'created') {
            await this.ensureAssignedIds();
            todos = todos.filter(t => !this.assignedTodoIds.has(t.id));
        }
        this.allTodos = [...this.allTodos, ...todos];
        this.nextToken = nextToken;
        this.hasMore = !!nextToken;
        this.isLoadingMore = false;
        this.applyFilters();
        this.cdr.markForCheck();
    }

    private async _fetchPage(token: string | null) {
        if (this.todoTab === 'assigned') {
            if (!this.currentUser?.id) return { todos: [], nextToken: null };
            return this.clientService.fetchTodosPage({
                userId: this.currentUser.id,
                nextToken: token,
                search: this.filterSearch || undefined,
                status: this.filterStatus || undefined,
                priority: this.filterPriority || undefined,
                tenantId: this.filterTenantId || undefined,
                clientId: this.filterClientId || undefined,
                backlog: this.filterBacklog || undefined,
            });
        }
        // Default: created by me
        return this.clientService.fetchTodosPage({
            creatorId: this.currentUser?.id,
            nextToken: token,
            search: this.filterSearch || undefined,
            status: this.filterStatus || undefined,
            priority: this.filterPriority || undefined,
            tenantId: this.filterTenantId || undefined,
            clientId: this.filterClientId || undefined,
            backlog: this.filterBacklog || undefined,
        });
    }

    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
    }

    onProjectSearch() {
        clearTimeout(this.projectSearchTimer);
        if (!this.projectSearch.trim()) {
            this.filteredProjects = [];
            return;
        }
        this.isSearchingProjects = true;
        this.projectSearchTimer = setTimeout(async () => {
            const tenantIds = this.tenants.map((t: any) => t.id);
            // Fetch all tenant-scoped projects from the server, then filter name client-side (case-insensitive)
            const all = await this.clientService.searchProjects(tenantIds);
            const q = this.projectSearch.toLowerCase();
            const matched = all.filter((p: any) =>
                p.projectName?.toLowerCase().includes(q) ||
                this.clients.find(c => c.id === p.clientId)?.name?.toLowerCase().includes(q)
            );
            this.filteredProjects = matched.map((p: any) => ({
                ...p,
                clientName: this.clients.find(c => c.id === p.clientId)?.name ?? '',
            }));
            this.isSearchingProjects = false;
            this.cdr.markForCheck();
        }, 300);
    }

    selectProject(p: any) {
        this.selectedProject = p;
        this.todoForm.projectId = p.id;
        this.todoForm.clientId = p.clientId;
        this.todoForm.tenantId = p.tenantId;
        this.projectSearch = p.projectName;
        this.filteredProjects = [];
    }

    clearProjectSelection() {
        this.selectedProject = null;
        this.todoForm.projectId = undefined;
        this.todoForm.clientId = undefined;
        this.todoForm.tenantId = undefined;
        this.projectSearch = '';
        this.filteredProjects = [];
    }

    switchToInternal() {
        if (this.todoType === 'internal') return;
        this.todoType = 'internal';
        this.todoForm.projectId = undefined;
        this.todoForm.clientId = undefined;
        this.selectedProject = null;
        this.projectSearch = '';
        this.filteredProjects = [];
        // Auto-select tenant if user only has one; keep existing if multiple
        if (this.tenants.length === 1) {
            this.todoForm.tenantId = this.tenants[0].id;
        } else if (this.tenants.length === 0) {
            this.todoForm.tenantId = undefined;
        }
    }

    switchToExternal() {
        if (this.todoType === 'external') return;
        this.todoType = 'external';
        this.selectedProject = null;
        this.projectSearch = '';
        this.filteredProjects = [];
        this.todoForm.projectId = undefined;
        this.todoForm.clientId = undefined;
        this.todoForm.tenantId = undefined;
        // Populate client list so the project picker works
        this.filteredClientsForTodo = this.clients;
    }

    // ── Filtering ──────────────────────────────────────────────────────────────

    /** allTodos is server-filtered; the project filter is applied client-side. */
    applyFilters() {
        let list = [...this.allTodos];
        if (this.filterProjectId) list = list.filter(t => t.projectId === this.filterProjectId);
        this.filtered = list;
    }

    /** Debounced handler for the search text input. */
    onSearchChange() {
        clearTimeout(this.filterSearchTimer);
        this.filterSearchTimer = setTimeout(() => this.loadTodos(), 400);
    }

    /** Instant handler for select-based filters (status, priority, tenant, client). */
    onFilterChange() {
        this.loadTodos();
    }

    setTodoTab(tab: 'created' | 'assigned') {
        if (this.todoTab === tab) return;
        this.todoTab = tab;
        this.loadTodos(); // reset pagination and re-fetch
    }

    clearFilters() {
        this.filterStatus = '';
        this.filterPriority = '';
        this.filterTenantId = '';
        this.filterClientId = '';
        this.filterProjectId = '';
        this.filterSearch = '';
        this.filterBacklog = false;
        this.loadTodos();
    }

    get hasActiveFilters(): boolean {
        return !!(this.filterStatus || this.filterPriority || this.filterTenantId || this.filterClientId || this.filterProjectId || this.filterSearch || this.filterBacklog);
    }

    // ── Cascade helpers ────────────────────────────────────────────────────────

    onTodoTenantChange() {
        this.filteredClientsForTodo = this.clients;
        this.todoForm.clientId = undefined;
        this.todoForm.projectId = undefined;
        this.filteredProjectsForTodo = [];
    }

    async onTodoClientChange() {
        this.todoForm.projectId = undefined;
        this.filteredProjectsForTodo = [];
        if (this.todoForm.clientId) {
            this.filteredProjectsForTodo = await this.clientService.getProjectsByClient(this.todoForm.clientId);
        }
    }

    // ── Date helpers ───────────────────────────────────────────────────────────

    onDueDateToggle() {
        if (this.hasDueDate) {
            if (!this.todoForm.dueDate) {
                const today = new Date();
                const y = today.getFullYear();
                const m = String(today.getMonth() + 1).padStart(2, '0');
                const d = String(today.getDate()).padStart(2, '0');
                this.todoForm.dueDate = `${y}-${m}-${d}`;
            }
        } else {
            this.todoForm.dueDate = '';
        }
    }

    private formatDueDate(): string | null {
        if (this.hasDueDate && this.todoForm.dueDate?.trim()) {
            let s = this.todoForm.dueDate.trim();
            if (!s.includes('T')) s = `${s}T12:00:00.000Z`;
            const d = new Date(s);
            return isNaN(d.getTime()) ? null : d.toISOString();
        }
        return null;
    }

    formatDateDisplay(iso: string | null | undefined): string {
        if (!iso) return '';
        return new Date(iso).toLocaleDateString('fr-BE', { day: '2-digit', month: 'short', year: 'numeric' });
    }

    // ── Modal ──────────────────────────────────────────────────────────────────

    openNew() {
        this.isEditing = false;
        this.hasDueDate = false;
        this.todoType = 'internal';
        this.filteredClientsForTodo = [];
        this.filteredProjectsForTodo = [];
        // Default to backlog: no tenant, client, or project pre-selected
        this.todoForm = { name: '', description: '', status: 'TODO', priority: 'MEDIUM', dueDate: '' };
        this.selectedUsers = this.currentUser ? [this.currentUser.id] : [];
        this.showModal = true;
    }

    openNew_external() {
        this.openNew();
        this.todoType = 'external';
        this.filteredClientsForTodo = this.clients;
        this.projectSearch = '';
        this.selectedProject = null;
        this.filteredProjects = [];
    }

    openEdit(todo: Todo) {
        this.isEditing = true;
        const rawDate = todo.dueDate;
        this.hasDueDate = !!rawDate;
        this.todoType = (todo.clientId || todo.projectId) ? 'external' : 'internal';
        this.filteredClientsForTodo = this.clients;
        this.filteredProjectsForTodo = [];
        this.todoForm = {
            id: todo.id,
            name: todo.name,
            description: todo.description ?? '',
            status: todo.status ?? 'TODO',
            priority: todo.priority ?? 'MEDIUM',
            dueDate: rawDate ? rawDate.split('T')[0] : '',
            tenantId: todo.tenantId ?? undefined,
            clientId: todo.clientId ?? undefined,
            projectId: todo.projectId ?? undefined,
        };
        // Load projects for selected client
        if (todo.clientId) {
            this.clientService.getProjectsByClient(todo.clientId).then(p => {
                this.filteredProjectsForTodo = p;
            });
        }
        this.selectedUsers = [];
        this.clientService.getUsersByTodo(todo.id).then(links => {
            this.selectedUsers = links.map((l: any) => l.userId);
        });
        this.showModal = true;
    }

    closeModal() {
        this.showModal = false;
        this.todoForm = {};
        this.selectedUsers = [];
    }

    // ── Read-only details view ("in-between" view) ──────────────────────────────

    /** Open the read-only details panel for a task (click on a list row). */
    openDetail(todo: Todo) {
        this.detailTodo = todo;
        this.detailAssignedUsers = [];
        this.showDetail = true;
        this.clientService.getUsersByTodo(todo.id).then((links: any[]) => {
            const ids = links.map(l => l.userId);
            this.detailAssignedUsers = this.users.filter((u: any) => ids.includes(u.id));
            this.cdr.markForCheck();
        });
    }

    closeDetail() {
        this.showDetail = false;
        this.detailTodo = null;
        this.detailAssignedUsers = [];
    }

    /** Jump from the details view into the edit form for the same task. */
    editFromDetail() {
        const todo = this.detailTodo;
        this.closeDetail();
        if (todo) this.openEdit(todo);
    }

    /** Request deletion from within the details view. */
    deleteFromDetail() {
        const todo = this.detailTodo;
        this.closeDetail();
        if (todo) this.requestDelete(todo);
    }

    /** Solid accent color for the details hero strip, keyed to status. */
    get detailStatusAccent(): string {
        switch (this.detailTodo?.status) {
            case 'IN_PROGRESS': return 'bg-blue-500';
            case 'DONE': return 'bg-emerald-500';
            default: return 'bg-slate-400';
        }
    }

    /** Human-readable distance to the details task's due date. */
    get detailDueRelative(): string {
        const due = this.detailTodo?.dueDate;
        if (!due) return '';
        const d = new Date(due);
        if (isNaN(d.getTime())) return '';
        const sod = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
        const diff = Math.round((sod(d) - sod(new Date())) / 86400000);
        if (diff === 0) return 'Today';
        if (diff === 1) return 'Tomorrow';
        if (diff === -1) return 'Yesterday';
        if (diff < 0) return `${-diff} days overdue`;
        return `In ${diff} days`;
    }

    /** True when the details task is past due and not done. */
    get detailIsOverdue(): boolean {
        const due = this.detailTodo?.dueDate;
        if (!due || this.detailTodo?.status === 'DONE') return false;
        const d = new Date(due);
        if (isNaN(d.getTime())) return false;
        const sod = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
        return sod(d) < sod(new Date());
    }

    getInitials(u: any): string {
        return `${(u?.firstName || u?.name || '?').charAt(0)}${u?.firstName && u?.name ? u.name.charAt(0) : ''}`.toUpperCase();
    }

    async saveTodo() {
        if (!this.todoForm.name?.trim()) return;
        if (this.todoType === 'external' && !this.todoForm.projectId) {
            alert('Please select a project for external todos.');
            return;
        }
        // For internal todos, clear external-only scope fields (client + project)
        // tenantId is kept: if set → scoped internal, if unset → backlog
        if (this.todoType === 'internal') {
            this.todoForm.clientId = undefined;
            this.todoForm.projectId = undefined;
        }
        try {
            if (this.isEditing && this.todoForm.id) {
                await this.clientService.updateTodo(this.todoForm.id, {
                    name: this.todoForm.name,
                    description: this.todoForm.description,
                    status: this.todoForm.status,
                    priority: this.todoForm.priority,
                    dueDate: this.formatDueDate(),
                    tenantId: this.todoForm.tenantId,
                    clientId: this.todoForm.clientId,
                    projectId: this.todoForm.projectId,
                });
                // Sync user assignments
                const currentLinks = await this.clientService.getUsersByTodo(this.todoForm.id);
                const currentIds = currentLinks.map((l: any) => l.userId);
                for (const uid of this.selectedUsers.filter(id => !currentIds.includes(id))) {
                    await this.clientService.linkTodoToUser(this.todoForm.id, uid);
                }
                for (const link of currentLinks.filter((l: any) => !this.selectedUsers.includes(l.userId))) {
                    await this.clientService.unlinkTodoFromUser(link.id);
                }
                // Notify newly assigned users
                const me = this.currentUser;
                if (me?.id) {
                    const senderName = [me.firstName, me.name].filter(Boolean).join(' ') || 'A colleague';
                    this.notificationService.notifyTodoAssignment({
                        todoName: this.todoForm.name || 'Untitled',
                        newUserIds: this.selectedUsers,
                        previousUserIds: currentIds,
                        currentUserId: me.id,
                        senderName,
                        todoId: this.todoForm.id,
                    }).catch(e => console.error('[Todos] Notification error:', e));
                }
            } else {
                const newTodo = await this.clientService.createTodo({
                    ...this.todoForm,
                    dueDate: this.formatDueDate(),
                    creatorId: this.currentUser?.id ?? undefined,
                });
                if (newTodo) {
                    for (const uid of this.selectedUsers) {
                        await this.clientService.linkTodoToUser(newTodo.id, uid);
                    }
                    // Notify newly assigned users
                    const me = this.currentUser;
                    if (me?.id) {
                        const senderName = [me.firstName, me.name].filter(Boolean).join(' ') || 'A colleague';
                        this.notificationService.notifyTodoAssignment({
                            todoName: this.todoForm.name || 'Untitled',
                            newUserIds: this.selectedUsers,
                            previousUserIds: [],
                            currentUserId: me.id,
                            senderName,
                            todoId: newTodo.id,
                        }).catch(e => console.error('[Todos] Notification error:', e));
                    }
                }
            }
            this.closeModal();
            await this.refreshAssignedIds(); // refresh assigned set before filtering
            await this.softRefreshTodos();
        } catch (e: any) {
            alert('Failed to save todo: ' + (e.message || e));
        }
    }

    // ── Delete ─────────────────────────────────────────────────────────────────

    async quickComplete(todo: Todo) {
        const newStatus = todo.status === 'DONE' ? 'TODO' : 'DONE';
        // Optimistic update: update locally first, no loading spinner
        todo.status = newStatus;
        if (newStatus === 'DONE') {
            (todo as any).validatedBy = this.currentUser?.id ?? null;
            (todo as any).validatedAt = new Date().toISOString();
        } else {
            (todo as any).validatedBy = null;
            (todo as any).validatedAt = null;
        }
        this.allTodos = [...this.allTodos];
        this.applyFilters();
        this.cdr.markForCheck();
        try {
            await this.clientService.updateTodo(todo.id, { status: newStatus });
        } catch (e: any) {
            // Revert on error
            todo.status = todo.status === 'DONE' ? 'TODO' : 'DONE';
            (todo as any).validatedBy = null;
            (todo as any).validatedAt = null;
            this.allTodos = [...this.allTodos];
            this.applyFilters();
            this.cdr.markForCheck();
            alert('Failed to update todo: ' + (e.message || e));
        }
    }

    requestDelete(todo: Todo) {
        this.todoToDelete = todo;
        this.showDeleteConfirm = true;
    }

    async handleDeleteConfirm(result: 'ok' | 'cancel') {
        this.showDeleteConfirm = false;
        if (result !== 'ok' || !this.todoToDelete) return;
        const deletedId = this.todoToDelete.id;
        // Optimistic removal
        this.allTodos = this.allTodos.filter(t => t.id !== deletedId);
        this.applyFilters();
        this.cdr.markForCheck();
        try {
            await this.clientService.deleteTodo(deletedId);
        } catch (e: any) {
            alert('Failed to delete todo: ' + (e.message || e));
            // Reload on error to restore correct state
            await this.loadTodos();
        }
        this.todoToDelete = null;
    }

    // ── Status / Priority helpers ──────────────────────────────────────────────

    statusLabel(s: string | null | undefined) {
        return ({ TODO: 'To Do', IN_PROGRESS: 'In Progress', DONE: 'Done' } as any)[s ?? ''] ?? s ?? '—';
    }

    statusColor(s: string | null | undefined) {
        return ({ TODO: 'bg-gray-100 text-gray-600', IN_PROGRESS: 'bg-blue-100 text-blue-700', DONE: 'bg-green-100 text-green-700' } as any)[s ?? ''] ?? 'bg-gray-100 text-gray-500';
    }

    priorityColor(p: string | null | undefined) {
        return ({ LOW: 'bg-gray-100 text-gray-600', MEDIUM: 'bg-amber-100 text-amber-700', HIGH: 'bg-red-100 text-red-700' } as any)[p ?? ''] ?? 'bg-gray-100 text-gray-500';
    }

    priorityLabel(p: string | null | undefined) {
        return ({ LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High' } as any)[p ?? ''] ?? 'Medium';
    }

    tenantName(id: string | null | undefined) {
        return this.tenants.find(t => t.id === id)?.name ?? null;
    }

    clientName(id: string | null | undefined) {
        return this.clients.find(c => c.id === id)?.name ?? null;
    }

    projectName(id: string | null | undefined) {
        return this.projects.find(p => p.id === id)?.projectName ?? null;
    }

    async loadProjectsForDisplay() {
        if (!this.tenants.length) return;
        const tenantIds = this.tenants.map((t: any) => t.id);
        const results = await this.clientService.searchProjects(tenantIds);
        this.projects = results.map((p: any) => ({ id: p.id, projectName: p.projectName }));
        this.cdr.markForCheck();
    }

    toggleUser(userId: string) {
        const idx = this.selectedUsers.indexOf(userId);
        if (idx >= 0) this.selectedUsers.splice(idx, 1);
        else this.selectedUsers.push(userId);
    }

    isUserSelected(userId: string) {
        return this.selectedUsers.includes(userId);
    }

    /**
     * Users offered in the "Assign to" picker: active ones only, plus any
     * archived user already assigned so an edit doesn't drop them.
     * `users` itself keeps everybody so past validators still resolve to a name.
     */
    get assignableUsers() {
        return this.users.filter(u => !u.isArchived || (u.id ? this.selectedUsers.includes(u.id) : false));
    }

    getValidatedByName(userId: string | null | undefined): string {
        if (!userId) return '';
        const emp = this.users.find((e: any) => e.id === userId);
        if (emp) return `${emp.firstName ?? ''} ${emp.name ?? ''}`.trim();
        return 'Unknown';
    }

    /** Count of loaded tasks that are not done. */
    get activeCount(): number {
        return this.filtered.filter(t => t.status !== 'DONE').length;
    }

    /** Count of loaded, not-done tasks due within the next 7 days. */
    get dueThisWeekCount(): number {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const end = start + 7 * 86400000;
        return this.filtered.filter(t => {
            if (t.status === 'DONE' || !t.dueDate) return false;
            const d = new Date(t.dueDate).getTime();
            return !isNaN(d) && d >= start && d <= end;
        }).length;
    }

    /** Due-date chip: "Today" / short date, flagged urgent when today or overdue (and not done). */
    getDueDisplay(due: string | null | undefined, status: string | null | undefined): { text: string; urgent: boolean } | null {
        if (!due) return null;
        const d = new Date(due);
        if (isNaN(d.getTime())) return null;
        const today = new Date();
        const sod = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
        const diff = Math.round((sod(d) - sod(today)) / 86400000);
        const done = status === 'DONE';
        if (diff === 0) return { text: 'Today', urgent: !done };
        if (diff < 0) return { text: this.shortDate(d), urgent: !done };
        return { text: this.shortDate(d), urgent: false };
    }

    private shortDate(d: Date): string {
        return d.toLocaleDateString('fr-BE', { day: 'numeric', month: 'short' });
    }

    /** Short display name for a user id, e.g. "Marc T." */
    getUserShortName(userId: string | null | undefined): string {
        if (!userId) return '';
        const u = this.users.find((e: any) => e.id === userId);
        if (!u) return '';
        const last = (u.name ?? '').charAt(0).toUpperCase();
        return `${u.firstName ?? ''}${last ? ' ' + last + '.' : ''}`.trim();
    }

    formatRelativeTime(dateString: string | null | undefined): string {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        const diffH = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMin < 1) return "à l'instant";
        if (diffMin < 60) return `il y a ${diffMin}min`;
        if (diffH < 24) return `il y a ${diffH}h`;
        if (diffDays === 1) return 'hier';
        if (diffDays < 7) return `il y a ${diffDays}j`;
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    }
}
